import { Box } from "@mui/material";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import React, { useEffect, useState } from "react";
import Divider from '@mui/material/Divider';

import {
  PaymentElement,
  LinkAuthenticationElement,
  ExpressCheckoutElement,
  PaymentMessage,
  ResponsiveForm,
  Spinner,
  StripeButton,
} from "./checkoutForm.styled";

export default function CheckoutForm() {

  const userDetails = JSON.parse(localStorage.getItem('currentUser') || "");

  const { REACT_APP_DEV } = process.env;

  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent?.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: REACT_APP_DEV!,
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage(error.message || "");
    } else {
      setMessage("An unexpected error occurred.");
    }

    setIsLoading(false);
  };

  const onConfirm = async () => {
      if (!stripe) {
        // Stripe.js hasn't loaded yet.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }

      const clientSecret = new URLSearchParams(window.location.search).get(
        "payment_intent_client_secret"
      );

      if (!clientSecret) {
        return;
      }

      // @ts-ignore
      const {error: submitError} = await elements.submit();
      if (submitError) {
        setMessage(submitError.message || "");
        return;
      }

      // Confirm the PaymentIntent using the details collected by the Express Checkout Element
      const {error} = await stripe.confirmPayment({
        // `elements` instance used to create the Express Checkout Element
        // @ts-ignore
        elements,
        // `clientSecret` from the created PaymentIntent
        clientSecret,
        confirmParams: {
          // @ts-ignore
          return_url: REACT_APP_DEV,
        },
      });

      if (error) {
        // This point is only reached if there's an immediate error when
        // confirming the payment. Show the error to your customer (for example, payment details incomplete)
        setMessage(error.message || "");
      } else {
        // The payment UI automatically closes with a success animation.
        // Your customer is redirected to your `return_url`.
      }
    };

  const paymentElementOptions: any = {
    layout: "tabs",
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", marginTop: "24px" }}>
      <ResponsiveForm onSubmit={handleSubmit}>
        {<ExpressCheckoutElement onConfirm={onConfirm}/>}
        <Box sx={{ mt: 3,  mb: 3 }}>
          <Divider flexItem orientation="horizontal" />
        </Box>
        <LinkAuthenticationElement id="link-authentication-element"  options={{defaultValues: {email: userDetails?.email}}} />
        <PaymentElement id="payment-element" options={paymentElementOptions} />
        <StripeButton disabled={isLoading || !stripe || !elements} id="submit">
          <span id="button-text">
            {isLoading ? <Spinner></Spinner> : "Pay"}
          </span>
        </StripeButton>
        {/* Show any error or success messages */}
        {message && <PaymentMessage>{message}</PaymentMessage>}
      </ResponsiveForm>
    </Box>
  );
}
